<script setup lang="ts">
import { storeToRefs } from 'pinia'

import ProfileHeader from '~/components/influencer/profile/Header.vue'
import TransitionDirectionalSwipe from '~/components/transitions/directionalSwipe.vue'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import { Breakpoints } from '~/enums/breakpoints'

import type { Influencer } from '~/types/influencer'
import type InfluencerInteractions from '~/types/influencerInteraction'

type Props = {
  influencer: Influencer
  interactions: InfluencerInteractions[]
  suggested: Influencer['id'][]
}

const props = defineProps<Props>()

const { id: INF_ID } = storeToRefs(useUserInfluencerStore())
const { IS_INF, IS_BAND } = storeToRefs(useUserStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const showAbout = ref(true)
const showInteraction = ref(false)

const userIsOwner = computed<boolean>(
  () => IS_INF.value && INF_ID.value === props.influencer.id,
)

const hasInteraction = computed<boolean>(
  () => props.interactions?.length > 0 && IS_BAND.value,
)

function toggle(): void {
  showAbout.value = !showAbout.value
  showInteraction.value = !showInteraction.value
}
</script>

<template>
  <div
    id="ignoreFontUpscale"
    class="influencerProfileMainWrapper ignoreFontUpscale tw-relative tw-mb-32 tw-w-full"
  >
    <!-- N.B: component hidden for Curators for now -->
    <!-- <div class="mainParentContainer">
      <ForceCurator v-if="userIsOwner" :influencer="influencer" />
    </div> -->
    <ProfileHeader
      :influencer="influencer"
      :user-is-owner="userIsOwner"
      :has-interaction="hasInteraction"
      :show-about="showAbout"
      :show-interaction="showInteraction"
      @toggle="toggle"
    />
    <TransitionDirectionalSwipe
      :direction="showAbout ? 'left' : 'right'"
      mode="out-in"
    >
      <KeepAlive>
        <article
          v-if="showAbout"
          key="about"
          class="mainParentContainer tw-pt-10"
          :influencer="influencer"
        >
          <!-- Mobile & Tablet -->
          <LazyInfluencerProfileAboutMobile
            v-if="SCREEN_WIDTH < Breakpoints.lg"
            :influencer="influencer"
            class="tw-block lg:tw-hidden"
          />
          <!-- Desktop -->
          <LazyInfluencerProfileAboutDesktop
            v-else
            :influencer="influencer"
            class="tw-hidden lg:tw-block"
          />
        </article>
        <LazyInfluencerProfileInteractions
          v-else-if="showInteraction && hasInteraction"
          key="interactions"
          :influencer="influencer"
          :interactions="interactions"
        />
      </KeepAlive>
    </TransitionDirectionalSwipe>
    <div class="mainParentContainer">
      <LazyVisibleInfluencerProfileAboutSuggestion
        v-if="!userIsOwner"
        :influencer="influencer"
        :suggested="suggested"
        hydrate-on-visible
      />
    </div>
  </div>
</template>
